import 'what-input';
import {
  type DataDogConfig,
  type SentryAppConfig,
  StimulusAppBootstrapper,
  segmentInitializer,
  startAnalytics,
  track,
} from '@betterment/js-runtime';
import { registerSambaControllers } from '@betterment/samba-stimulus';
import { deferredTestTrack } from '@betterment/shared-components';
import * as TestTrack from '@betterment-oss/test-track';
import * as Sentry from '@sentry/browser';
import { definitionsFromGlob } from 'stimulus-vite-helpers';

import appConfig from './src/Shared/appConfig';
import recordSessionLatency from './src/Shared/latency-logger';
import { buildCustomPageLoadedProperties } from './src/Shared/segment';

import type { AnalyticsProvider } from '@betterment-oss/test-track';
import './src/index.css';

const controllers = import.meta.glob(['./src/**/*-controller.ts', './src/**/*-controller.tsx'], { eager: true });
const definitions = definitionsFromGlob(controllers);

const sentryAppConfig: SentryAppConfig = {
  sentryApiToken: appConfig.sentryApiToken || '',
  sentryEnvironment: appConfig.sentryEnvironment || 'development',
  sentryRelease: appConfig.sentryRelease || '',
  isReplayEnabled: Boolean(appConfig.isSessionReplayEnabled),
  sentryErrorReplaySampleRate: 1,
  user: appConfig.user || {},
  sentryWhitelistUrls: ['betterment.com'],
};

const dataDogConfig: DataDogConfig = {
  applicationId: appConfig.dataDogApplicationId ?? '',
  clientToken: appConfig.dataDogClientToken ?? '',
  env: appConfig.dataDogEnv || 'development',
  site: 'datadoghq.com',
  service: 'retail',
  replaySampleRate: 0,
  isRumEnabled: Boolean(appConfig.dataDogEnabled) && appConfig.dataDogEnv === 'production',
  isLoggingEnabled: appConfig.dataDogEnv === 'production',
  allowedTracingUrls: [/https:\/\/*\.betterment\.com/],
};

const testTrackAnalytics: AnalyticsProvider = {
  trackAssignment(_visitorId, assignment, callback) {
    track(
      'SplitAssigned',
      {
        splitName: assignment.splitName,
        splitVariant: assignment.variant ?? '',
        splitContext: assignment.context ?? undefined,
      },
      () => callback(true)
    );
  },
  alias: () => undefined,
  identify: () => undefined,
};

export { appConfig };

export async function startStimulusApplication() {
  if (appConfig.segmentWriteKey) {
    segmentInitializer(appConfig.segmentWriteKey);
  }

  void TestTrack.initialize({
    analytics: testTrackAnalytics,
    errorLogger: message => Sentry.captureException(`test_track ${message}`),
  }).then(testTrack => {
    startAnalytics(
      testTrack.visitorId,
      appConfig.analyticsTrackerDefaultProperties.application,
      buildCustomPageLoadedProperties()
    );

    deferredTestTrack.resolve(testTrack);
  });

  const bootstrapper = new StimulusAppBootstrapper(definitions, sentryAppConfig, dataDogConfig);

  if (bootstrapper.dataDogInitializer?.datadogLogs) {
    const { logger } = bootstrapper.dataDogInitializer.datadogLogs;

    const isProductionEnv = dataDogConfig.env === 'production';
    recordSessionLatency(logger, isProductionEnv, appConfig.dataDogMetricsTags);
  }

  if (bootstrapper.dataDogInitializer?.datadogRum) {
    bootstrapper.dataDogInitializer.datadogRum.setUser({ id: appConfig.user?.retail_user_id });
    bootstrapper.dataDogInitializer.datadogRum.setGlobalContextProperty(
      'retail_user_id',
      appConfig.user?.retail_user_id
    );
  }

  const application = await bootstrapper.initialize();
  registerSambaControllers(application);
}
